import { useState, useEffect } from 'react'
import { supabase } from '../api/supabase'

export interface PermissionGroup {
  id: string
  name: string
  description: string | null
  is_system: boolean
  is_active: boolean
  created_at: string
  member_count?: number
}

export interface Permission {
  id: string
  code: string
  description: string
  module: string
  is_active: boolean
}

export function usePermissionGroups() {
  const [groups, setGroups] = useState<PermissionGroup[]>([])
  const [loading, setLoading] = useState(false)

  const fetchGroups = async () => {
    setLoading(true)
    const { data: groupData } = await supabase
      .from('permission_groups')
      .select('*')
      .order('name')

    if (!groupData) { setLoading(false); return }

    const { data: memberRows } = await supabase
      .from('user_roles')
      .select('group_id')
      .not('group_id', 'is', null)

    const counts: Record<string, number> = {}
    memberRows?.forEach(row => {
      if (row.group_id) counts[row.group_id] = (counts[row.group_id] || 0) + 1
    })

    setGroups(groupData.map(g => ({ ...g, member_count: counts[g.id] || 0 })))
    setLoading(false)
  }

  useEffect(() => { fetchGroups() }, [])

  const deleteGroup = async (id: string) => {
    await supabase.from('permission_groups').delete().eq('id', id)
    await fetchGroups()
  }

  const saveGroup = async (group: Partial<PermissionGroup> & { name: string }) => {
    if (group.id) {
      await supabase
        .from('permission_groups')
        .update({ name: group.name, description: group.description ?? null })
        .eq('id', group.id)
    } else {
      await supabase.from('permission_groups').insert({
        name: group.name,
        description: group.description ?? null,
        is_system: false,
        is_active: true,
      })
    }
    await fetchGroups()
  }

  return { groups, loading, deleteGroup, saveGroup, refetch: fetchGroups }
}

export function useAllPermissions() {
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    supabase
      .from('permissions')
      .select('*')
      .eq('is_active', true)
      .order('module')
      .order('description')
      .then(({ data }) => {
        setPermissions(data || [])
        setLoading(false)
      })
  }, [])

  return { permissions, loading }
}

export function useGroupPermissionCodes(groupId: string | null) {
  const [codes, setCodes] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)

  const fetch = async (id: string) => {
    setLoading(true)
    const { data } = await supabase
      .from('group_permissions')
      .select('permission_code')
      .eq('group_id', id)
    setCodes(new Set(data?.map(r => r.permission_code) || []))
    setLoading(false)
  }

  useEffect(() => {
    if (groupId) fetch(groupId)
    else { setCodes(new Set()); setLoading(false) }
  }, [groupId])

  const syncPermissions = async (id: string, selectedCodes: Set<string>) => {
    await supabase.from('group_permissions').delete().eq('group_id', id)
    if (selectedCodes.size > 0) {
      await supabase.from('group_permissions').insert(
        Array.from(selectedCodes).map(code => ({ group_id: id, permission_code: code }))
      )
    }
    await fetch(id)
  }

  return { codes, loading, syncPermissions }
}
